import type { AiChatConversationContext } from './aiChatContextTypes';
import type { ChatOpenConversationMode } from './aiChatTypes';
import type { AiChatPrompt, AiChatPromptSetId } from './prompts/aiChatPromptTypes';
import { buildConversationScopeKey, type ConversationScopeMode } from './prompts/buildConversationScopeKey';

const getStringContextField = (context: AiChatConversationContext | undefined, key: string): string | undefined => {
  const value = context?.data[key];
  return typeof value === 'string' ? value : undefined;
};

export type BuildAiContextualPromptChipTrackingPropsArgs = {
  prompt: AiChatPrompt;
  source: string;
  promptSet: AiChatPromptSetId;
  context?: AiChatConversationContext;
  conversationMode: ChatOpenConversationMode;
  userMessage: string;
  scopeKey?: string;
};

export type AiContextualPromptChipTrackingProps = {
  source: string;
  promptSet: AiChatPromptSetId;
  displayPrompt: string;
  useWebSearch: boolean;
  conversationMode: ChatOpenConversationMode;
  userMessage: string;
  hasContext: boolean;
  contextLabel?: string;
  focusSymbol?: string;
  focusName?: string;
  scopeKey?: string;
};

export const buildAiContextualPromptChipTrackingProps = ({
  prompt,
  source,
  promptSet,
  context,
  conversationMode,
  userMessage,
  scopeKey,
}: BuildAiContextualPromptChipTrackingPropsArgs): AiContextualPromptChipTrackingProps => {
  const resolvedScopeKey = scopeKey ?? buildConversationScopeKey(source, context);

  return {
    source,
    promptSet,
    displayPrompt: prompt.displayPrompt,
    useWebSearch: prompt.useWebSearch ?? false,
    conversationMode,
    userMessage,
    hasContext: Boolean(context),
    contextLabel: context?.label,
    focusSymbol: getStringContextField(context, 'symbol'),
    focusName: getStringContextField(context, 'name'),
    scopeKey: resolvedScopeKey,
  };
};

export type BuildAiContextualPromptChipAskSomethingElseTrackingPropsArgs = {
  source: string;
  promptSet: AiChatPromptSetId;
  context?: AiChatConversationContext;
  conversationScope?: ConversationScopeMode;
  scopeKey?: string;
};

export const buildAiContextualPromptChipAskSomethingElseTrackingProps = ({
  source,
  promptSet,
  context,
  conversationScope = 'entity',
  scopeKey,
}: BuildAiContextualPromptChipAskSomethingElseTrackingPropsArgs) => {
  const resolvedScopeKey = scopeKey ?? buildConversationScopeKey(source, context, conversationScope);

  return {
    source,
    promptSet,
    hasContext: Boolean(context),
    contextLabel: context?.label,
    focusSymbol: getStringContextField(context, 'symbol'),
    focusName: getStringContextField(context, 'name'),
    scopeKey: resolvedScopeKey,
  };
};
